import React from 'react';
import Header from '@/components/Header';
import ManufacturerLogos from '@/components/ManufacturerLogos';
import Footer from '@/components/sections/Footer';

export default function TermsOfServicePage() {
  return (
    <div className="min-h-screen">
      <Header />
      <ManufacturerLogos />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl sm:text-5xl font-bold mb-6">Terms of Service</h1>
          <p className="text-muted-foreground mb-8">Last updated: January 2025</p>

          <div className="prose prose-lg max-w-none space-y-8">
            <section>
              <h2 className="text-2xl font-bold mb-4">1. Acceptance of Terms</h2>
              <p className="text-muted-foreground leading-relaxed">
                By accessing or using UAVsForSale.com (the "Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, do not use the Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">2. Description of Service</h2>
              <p className="text-muted-foreground leading-relaxed">
                UAVsForSale.com is a marketplace platform that connects buyers and sellers of industrial drones and unmanned aerial vehicles (UAVs). We act as an intermediary and broker to facilitate transactions but are not a party to the actual sale agreements between buyers and sellers.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">3. User Accounts and Registration</h2>
              <h3 className="text-xl font-semibold mb-3">3.1 Account Creation</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                To use certain features of the Service, you may need to create an account. You agree to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>Provide accurate, current, and complete information</li>
                <li>Maintain and update your information</li>
                <li>Keep your account credentials secure</li>
                <li>Be responsible for all activities under your account</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">3.2 Account Eligibility</h3>
              <p className="text-muted-foreground leading-relaxed">
                You must be at least 18 years old and have the legal authority to enter into contracts to use this Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">4. Listings and Transactions</h2>
              <h3 className="text-xl font-semibold mb-3">4.1 Seller Obligations</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                If you list a drone for sale, you warrant that:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>You have the legal right to sell the item</li>
                <li>All listing information is accurate and truthful</li>
                <li>The drone complies with applicable laws and regulations</li>
                <li>You will honor the terms of any sale agreement</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">4.2 Buyer Obligations</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                If you inquire about or purchase a drone, you agree to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>Conduct due diligence before purchasing</li>
                <li>Comply with all applicable drone operation regulations</li>
                <li>Complete transactions in good faith</li>
                <li>Resolve disputes directly with the seller</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">4.3 Our Role</h3>
              <p className="text-muted-foreground leading-relaxed">
                UAVsForSale.com facilitates connections between buyers and sellers but is not responsible for the actual transactions, drone condition, or fulfillment. We do not guarantee the accuracy of listings or the conduct of users.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">5. Prohibited Activities</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                You may not use the Service to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>Post false, misleading, or fraudulent listings</li>
                <li>Violate any laws or regulations</li>
                <li>Infringe on intellectual property rights</li>
                <li>Harass, abuse, or harm other users</li>
                <li>Interfere with the Service's operation</li>
                <li>Use automated systems to access the Service without permission</li>
                <li>List stolen or illegally obtained drones</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">6. Intellectual Property</h2>
              <p className="text-muted-foreground leading-relaxed">
                The Service and its content (excluding user-generated content) are owned by UAVsForSale.com and protected by copyright, trademark, and other intellectual property laws. You may not reproduce, distribute, or create derivative works without our written permission.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">7. Disclaimers and Limitation of Liability</h2>
              <h3 className="text-xl font-semibold mb-3">7.1 Service "As Is"</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                The Service is provided "as is" and "as available" without warranties of any kind, either express or implied.
              </p>

              <h3 className="text-xl font-semibold mb-3">7.2 Limitation of Liability</h3>
              <p className="text-muted-foreground leading-relaxed">
                To the maximum extent permitted by law, UAVsForSale.com shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">8. Indemnification</h2>
              <p className="text-muted-foreground leading-relaxed">
                You agree to indemnify and hold harmless UAVsForSale.com from any claims, damages, losses, or expenses arising from your use of the Service, your listings, or your violation of these Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">9. Dispute Resolution</h2>
              <p className="text-muted-foreground leading-relaxed">
                Any disputes arising from these Terms or the Service shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association. The arbitration shall take place in San Francisco, California.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">10. Termination</h2>
              <p className="text-muted-foreground leading-relaxed">
                We reserve the right to suspend or terminate your access to the Service at any time, with or without cause or notice, for any violation of these Terms or for any other reason.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">11. Changes to Terms</h2>
              <p className="text-muted-foreground leading-relaxed">
                We may modify these Terms at any time. Continued use of the Service after changes constitutes acceptance of the modified Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">12. Governing Law</h2>
              <p className="text-muted-foreground leading-relaxed">
                These Terms are governed by the laws of the State of California, without regard to its conflict of law principles.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">13. Contact Information</h2>
              <p className="text-muted-foreground leading-relaxed">
                For questions about these Terms, contact us at:
              </p>
              <div className="mt-4 text-muted-foreground">
                <p><strong>Email:</strong> legal@uavsforsale.com</p>
                <p><strong>Address:</strong> UAVsForSale.com, San Francisco, CA</p>
              </div>
            </section>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}